import TR from 'utils/TR';
import Header from 'components/header';
import T from 'utils/T';
import ex from 'utils/ex';
import SimulationConditions from './SimulationConditions';
import SimulationResult from './SimulationResult';
import PageTitle from '../PageTitle';
import Segment from 'components/common/Segment';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import SimulationInfo from './SimulationInfo';
import React from 'react';
import PageContent from '../PageContent';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useSimulationActive from 'hooks/communication/useSimulationActive';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useSimulationActiveResult from 'hooks/communication/useSimulationActiveResult';
import useSimulationAgpGraph from 'hooks/communication/useSimulationAgp';

export default function SimulationExercise(props) {
  const {
    renderAll,
    front,
    ...rest
  } = props;

  const [added_step_cnt, setAddedStepCnt] = React.useState(2000);
  const onSelect = React.useCallback((index) => () => {
    setAddedStepCnt(() => index);
  }, []);
  const [added_step_hash, setAddedStepHash] = React.useState({});
  const onSteps = React.useCallback((list, value) => () => {
    // data must be `${time_zone_tp_cd}:${weekday_tp_cd}`
    setAddedStepHash(hash => ({
      ...list.reduce((a, c) => ({ ...a, [c]: value !== undefined ? value : !a[c] }), value !== undefined ? {} : hash),
    }));
  }, []);

  const {
    requestSimulationActive,
  } = useSimulationActive();

  const [data, setData] = React.useState({ uninitialized: true });
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    if (!patient_no) return;
    setData(() => ({ uninitialized: true }));
    requestSimulationActive({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
    }, (success, data) => {
      if (success) {
        setData(() => data);
      } else {
        setData(() => ({ uninitialized: true }));
      }
    });
  }, [patient_no, period_end, period_start, requestSimulationActive]);

  const {
    requestSimulationActiveResult,
  } = useSimulationActiveResult();

  const added_step_list = React.useMemo(() => {
    const list = [];
    Object.keys(added_step_hash).forEach(key => {
      if (!added_step_hash[key]) return;
      const [time_zone_tp_cd, weekday_tp_cd] = key.split(':');
      list.push({
        time_zone_tp_cd,
        weekday_tp_cd,
      });
    });
    return list;
  }, [added_step_hash]);

  const [agp, setAgp] = React.useState([]);
  const {
    requestSimulationAgpGraph,
  } = useSimulationAgpGraph();

  const [result, setResult] = React.useState({ uninitialized: true });

  React.useEffect(() => {
    setAgp(() => []);
    setResult(() => ({ uninitialized: true }));
  }, [added_step_cnt, added_step_hash]);

  const onExecute = React.useCallback(() => {
    if (!patient_no) return;
    setAgp(() => []);
    setResult(() => ({}));

    requestSimulationAgpGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      ai_simulation_tp_cd: 'A',
      added_step_cnt,
      added_step_list,
    }, (success, data) => {
      if (success) {
        setAgp(() => data?.agp_graph_list || []);
      } else {
        setAgp(() => []);
      }
    });

    requestSimulationActiveResult({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      added_step_cnt: added_step_cnt,
      added_step_list,
    }, (success, data) => {
      if (success) {
        setResult(() => data);
      } else {
        setResult(() => ({}));
      }
    });
  }, [requestSimulationAgpGraph, patient_no, period_start, period_end, added_step_cnt, added_step_list, requestSimulationActiveResult]);

  return (
    <VBox {...ex(['Page', 'SimulationExercise'], rest)}>
      <PageContent>
        <VBox full>
          <HBox {...ex('Top')}>
            <Segment full>
              <SimulationConditions
                {...{
                  selected: added_step_cnt,
                  onSelect,
                  data,
                  steps: added_step_hash,
                  onSteps,
                  onExecute,
                  disabled: !patient_no || data.uninitialized === true,
                }} />
            </Segment>
            <Segment>
              <SimulationInfo {...{ data }} />
            </Segment>
          </HBox>
          <Segment {...{ direction: DIRECTION_VERTICAL }}>
            <SimulationResult {...{ data, result, agp }} />
          </Segment>
        </VBox>
      </PageContent>
    </VBox>
  );
}
